'use client';

import { useFormHelper } from '@plura/client/utils';
import cn from "classnames";
import React, { useEffect } from 'react';
import type { PlacesType } from 'react-tooltip';
import { isMobile } from 'react-device-detect';
import { Icon } from "@plura/client/components";
import Tooltip from '../../atoms/Tooltip';

interface Option {
  value: string;
  label: string;
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement> &
{
  label?: string;
  error?: string;
  className?: string;
  classLabel?: string;
  classInput?: string;
  icon?: string;
  copy?: boolean;
  tooltip?: string;
  place?: PlacesType;
  trailingComponent?: React.ReactNode;
  options: Option[];
  disabledKeySelection?: boolean;
  handleOnChange?: () => void;
};

const Checkbox = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    label,
    className,
    classLabel,
    classInput,
    icon,
    copy,
    tooltip,
    place,
    trailingComponent,
    options,
    disabledKeySelection,
    handleOnChange,
    ...rest
  } = props;
  const { onValueChange, errorMessage, ...formHelperProps } = useFormHelper(rest);

  const getCurrentValuesArray = (value: string | string[] | number | readonly string[] | undefined): string[] => {
    if (Array.isArray(value)) {
      return value.map(String);
    } else if (typeof value === 'string' && value) {
      return value.split(',').filter(v => v.trim());
    } else if (typeof value === 'number') {
      return [String(value)];
    }
    return [];
  };

  const isValueSelected = (optionValue: string): boolean => {
    return getCurrentValuesArray(formHelperProps.value).includes(optionValue);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isMobile && !disabledKeySelection) {
        if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
          return;
        }

        const el = document.activeElement;
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el as HTMLElement).isContentEditable)) {
          return;
        }

        const option = options.find(opt => opt.value === event.key);

        if (option) {
          const checkbox = document.getElementById(`${rest.id}.${option.value}`) as HTMLInputElement;

          if (checkbox) {
            checkbox.click();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [options, onValueChange, rest.id, disabledKeySelection]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;

    const currentArray = getCurrentValuesArray(formHelperProps.value);

    let newArray: string[];
    if (checked) {
      newArray = currentArray.includes(value) ? currentArray : [...currentArray, value];
    } else {
      newArray = currentArray.filter(v => v !== value);
    }

    onValueChange(newArray.join(','));
    if (handleOnChange) setTimeout(() => handleOnChange(), 450);
  };

  return (
    <div
      className={cn(
        'w-full',
        className
      )}
    >
      {label && (
        <label className={cn(classLabel, 'mb-3.5 block text-sm font-semibold leading-[24px] text-[#33383f] [.dark-mode_&]:text-[#efefef]')}>
          {label}{" "}
          {tooltip && (
            <Tooltip
              className="relative -top-px [&_svg]:fill-[#9a9fa5]"
              content={tooltip}
              place={place ? place : "right"}
            />
          )}
        </label>
      )}
      <div className="relative">
        <div className="flex w-full flex-row flex-wrap gap-1.5 max-md:gap-1.5">
          {options.map((option) => (
            <div
              key={option.value}
              className="relative w-full max-md:w-auto"
            >
              <input
                id={`${rest.id}.${option.value}`}
                type="checkbox"
                {...formHelperProps}
                name={rest.id}
                value={option.value}
                checked={isValueSelected(option.value)}
                onChange={handleCheckboxChange}
                className="absolute left-0 top-0 z-[-1] h-0 w-0 opacity-0"
              />
              <label htmlFor={`${rest.id}.${option.value}`} className="flex cursor-pointer select-none items-center gap-2 transition-colors duration-200 ease-out">
                <div
                  className={cn(
                    'flex size-[18px] shrink-0 items-center justify-center border-2 bg-transparent transition-all duration-200 ease-out',
                    'border-[color:var(--answer-color,var(--button-color))] [border-radius:calc(var(--border-radius)/2)]',
                    'hover:border-[color:var(--answer-color,var(--button-color))]',
                    isValueSelected(option.value) && 'border-[color:var(--answer-color,var(--button-color))] bg-[color:var(--answer-color,var(--button-color))] text-[color:var(--answer-text-color,var(--button-text-color))]',
                    !!errorMessage && 'border-[#ff6a55] hover:border-[#ff6a55]',
                  )}
                >
                  {isValueSelected(option.value) && (
                    <Icon name="check" className="!size-3" />
                  )}
                </div>
                <span className="text-sm leading-[1.4] text-[color:var(--text-color)]">{option.label}</span>
              </label>
            </div>
          ))}
        </div>

        {icon && (
          <div className={cn('icon absolute bottom-0 left-0 top-0 flex h-12 w-12 items-center justify-center')}>
            <i className={icon} />{" "}
          </div>
        )}
        {copy && (
          <button type="button" className="absolute bottom-0 right-0 top-0 flex h-12 w-12 items-center justify-center [&_svg]:fill-[#6f767e] hover:[&_svg]:fill-[#2a85ff]">
            <Icon name="copy" className="!size-6" />{" "}
          </button>
        )}

        {
          trailingComponent && (
            <div className="absolute right-1 top-1 z-[2]">
              {trailingComponent}
            </div>
          )
        }

        {errorMessage && (
          <p className="mt-1 flex items-center justify-end gap-1.5 text-left text-[13px] font-medium leading-[1.3] text-[#ff6a55]">
            <Icon name="info" className="!size-3.5 shrink-0" />
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox
